/**
 * Per-date-window stay model — pure, no I/O (tested with `node --test`).
 *
 * VESTA sells its next ~90 days on a 2-night minimum and later dates on a 1-night
 * minimum, with a different length-of-stay curve for each (95001: 7 nights −19%
 * near, −14% far). Its price job sweeps the two windows on different nights; until
 * 2026-10-05 each run overwrote the unit's single `min_nights` and single curve,
 * so the values flipped every week. The job now writes one row per window
 * (`unit_stay_model_windows`) and the site picks the window that contains the
 * CHECK-IN date. No window → the unit-level values, exactly as before.
 */

import type { LosCurve } from "./los-discount.ts";
import type { MinStayRange } from "./min-stay.ts";

export interface StayWindow {
  /** inclusive check-in, yyyy-mm-dd */
  from: string;
  /** exclusive */
  to: string;
  min: number | null;
  /** null = this window has no curve of its own; use the unit's. */
  los: LosCurve | null;
  measuredAt: string;
}

/** The window governing a check-in: the most recently measured one containing it. */
export function windowFor(windows: StayWindow[] | null | undefined, checkinIso: string | null | undefined): StayWindow | null {
  if (!windows?.length || !checkinIso) return null;
  let best: StayWindow | null = null;
  for (const w of windows) {
    if (checkinIso < w.from || checkinIso >= w.to) continue;
    if (!best || w.measuredAt > best.measuredAt) best = w;
  }
  return best;
}

/** The curve to price a stay with: its window's, else the unit's. */
export function pickLosCurve(
  unitCurve: LosCurve | null | undefined,
  windows: StayWindow[] | null | undefined,
  checkinIso: string | null | undefined,
): LosCurve | null {
  const w = windowFor(windows, checkinIso);
  return w?.los && Object.keys(w.los).length ? w.los : unitCurve ?? null;
}

/**
 * Windows as minimum-stay ranges for `minNightsForCheckin` (first match wins,
 * so newest first). `to` becomes inclusive, the shape that helper expects.
 */
export function minStayRangesFromWindows(windows: StayWindow[] | null | undefined): MinStayRange[] {
  return [...(windows ?? [])]
    .filter((w) => typeof w.min === "number" && w.min > 0 && w.to > w.from)
    .sort((a, b) => (a.measuredAt < b.measuredAt ? 1 : -1))
    .map((w) => {
      const last = new Date(Date.parse(`${w.to}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
      return { from: w.from, to: last, min: w.min as number };
    });
}
